import DropdownMenu from "../../../Monolith-UI/DropdownMenu/DropdownMenu.js";
import { usePermissions } from "../../../hooks/usePermissions";
import { useState, useEffect } from "react";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { MonitorSmartphone } from "lucide-react";
import styled from "styled-components";
import { Evidence, EvidenceQuery } from "@/Features";

const PageSize = 100;

const MenuItem = styled(({ className, evidenceRecord }) => {
  return (
    <div className={className}>
      <div className={"evidence-number"}>{evidenceRecord.evidence_number}</div>
      <div className={"evidence-type"}>{evidenceRecord.item_type}</div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  gap: 5px;

  padding: 5px 0px;

  .evidence-number {
    font-weight: 400;
  }

  .evidence-type {
    font-weight: 300;
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const EvidenceSelector = ({
  case_id,
  value,
  onSelect,
  showLabel = true,
  variant = "contained",
  error = false,
  disabled = false,
}: {
  case_id?: string;
  value: Evidence;
  onSelect: (evidence: Evidence) => void;
  showLabel?: boolean;
  variant?: "contained" | "outlined";
  error?: boolean;
  disabled?: boolean;
}) => {
  const { currentUser } = usePermissions();
  const [selectedEvidence, setSelectedEvidence] = useState(value);
  const [search, setSearch] = useState("");
  const [searchValue] = useDebounce(search, 300);

  const query = {
    search: searchValue,
    pageSize: PageSize,
    case_id: case_id ? Number(case_id) : undefined,
  };

  useEffect(() => {
    setSelectedEvidence(value);
  }, [value]);

  const {
    data,
    isLoading,
    isError,
    error: evidenceError,
    isFetched,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = EvidenceQuery.useGetInfiniteEvidence(query);

  const records =
    data?.pages?.reduce<Evidence[]>((acc, page) => {
      return [...acc, ...(page?.data || [])];
    }, []) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const menuItems = [
    {
      radioGroup: true,
      itemTestId: "task-evidence-option",
      value: value?.uuid,
      items:
        records?.map((evidenceRecord: any) => {
          return {
            label: evidenceRecord.evidence_number,
            value: evidenceRecord.uuid,
            render: () => <MenuItem evidenceRecord={evidenceRecord} />,
            onClick: () => {
              setSelectedEvidence(evidenceRecord);
              onSelect?.(evidenceRecord);
            },
          };
        }) || [],
    },
  ];

  return (
    <DropdownMenu
      menuItems={menuItems}
      title={"Select Evidence"}
      defaultValue={undefined}
      dropdownContent={null}
      variant={variant}
      buttonProps={{ "data-testid": "task-evidence-select-button" }}
      searchEnabled={true}
      onScrollEnd={debouncedFetchNextPage}
      onSearch={setSearch}
      textColor={!!selectedEvidence?.evidence_id ? "primary" : "secondary"}
      loading={!data}
      error={error}
      disabled={disabled}
    >
      {showLabel && (
        <div title={selectedEvidence?.evidence_number || "Evidence"}>
          {
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                width: "100%",
              }}
            >
              <MonitorSmartphone size={14} style={{ minWidth: 14 }} />
              <div
                style={{
                  maxWidth: 125,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedEvidence?.evidence_number || "Evidence"}
              </div>
            </div>
          }
        </div>
      )}
    </DropdownMenu>
  );
};

export default EvidenceSelector;
